/**
 * Analytics Core
 * 
 * @atomic - Core analytics initialization and session management
 * @modular - Foundation for all analytics tracking
 * 
 * Purpose: Provides GA4 initialization and session management
 * Exports: initGA, trackEvent, trackPageView
 */

import { sessionStorage as safeSessionStorage } from '../storage'
import { createLogger } from '../logger'

const logger = createLogger('Analytics')

let isInitialized = false
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development'
const isDebugMode = window.ANALYTICS_DEBUG === true

// Session management
const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes

/**
 * Get or create session ID
 */
export const getOrCreateSessionId = () => {
  const stored = safeSessionStorage.get('analytics_session_id')
  const timestamp = safeSessionStorage.get('analytics_session_start')
  const now = Date.now()

  if (stored && timestamp && (now - parseInt(timestamp)) < SESSION_TIMEOUT) {
    safeSessionStorage.set('analytics_last_activity', now)
    return stored
  }

  // Create new session
  const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  safeSessionStorage.set('analytics_session_id', newSessionId)
  safeSessionStorage.set('analytics_session_start', now)
  safeSessionStorage.set('analytics_last_activity', now)
  safeSessionStorage.set('analytics_sequence_number', 0)
  
  if (import.meta.env.DEV || isDebugMode) {
    logger.info('New session created', { sessionId: newSessionId, timestamp: new Date(now).toISOString() })
  }
  
  return newSessionId
}

/**
 * Get next sequence number for event ordering
 */
export const getNextSequenceNumber = () => {
  const current = safeSessionStorage.get('analytics_sequence_number', 0)
  const next = current + 1
  safeSessionStorage.set('analytics_sequence_number', next)
  return next
}

/**
 * Get device context
 */
export const getDeviceContext = () => {
  const width = window.innerWidth
  let deviceType = 'desktop'
  if (width < 768) deviceType = 'mobile'
  else if (width < 1024) deviceType = 'tablet'

  return {
    device_type: deviceType,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    browser: navigator.userAgent.split(/[()]/)[1]?.split(';')[0] || 'unknown',
    os: navigator.platform || 'unknown',
    screen_resolution: `${window.screen.width}x${window.screen.height}`
  }
}

/**
 * Module-level user context cache
 * Updated when auth state changes
 */
let userContextCache = {
  user_segment: 'unknown',
  is_authenticated: false,
  account_age_days: null
}

/**
 * Module-level active experiments cache
 * Updated by AppConfigContext when variant assignments change
 */
let activeExperimentsCache = {}

/**
 * Update active experiments cache (called from AppConfigContext)
 */
export const updateExperimentsCache = (experiments) => {
  activeExperimentsCache = { ...experiments }
}

/**
 * Update user context
 * Call this when auth state changes
 */
export const updateUserContext = (user = null) => {
  if (user) {
    const accountCreatedDate = new Date(user.createdAt)
    const daysSinceCreation = Math.floor((Date.now() - accountCreatedDate.getTime()) / (1000 * 60 * 60 * 24))
    
    userContextCache = {
      user_segment: user.role || 'user',
      is_authenticated: true,
      account_age_days: daysSinceCreation
    }
    
    if (import.meta.env.DEV || isDebugMode) {
      logger.info('User context updated', userContextCache)
    }
  } else {
    userContextCache = {
      user_segment: 'unknown',
      is_authenticated: false,
      account_age_days: null
    }
    
    if (import.meta.env.DEV || isDebugMode) {
      logger.info('User context cleared')
    }
  }
}

/**
 * Get user context
 */
export const getUserContext = () => {
  return { ...userContextCache }
}

/**
 * Log detailed analytics event in debug mode
 */
export const logDebugEvent = (eventType, eventData) => {
  if (!isDebugMode && !isDevelopment) return

  const timestamp = new Date().toISOString()
  const enhancedData = {
    ...eventData,
    _timestamp: timestamp,
    _session_id: getOrCreateSessionId(),
    _sequence: getNextSequenceNumber(),
    _device: getDeviceContext(),
    _user: getUserContext()
  }

  if (isDebugMode) {
    console.group(`📊 Analytics [${eventType}] - ${timestamp}`)
    console.table(enhancedData)
    console.groupEnd()
  } else if (import.meta.env.DEV) {
    logger.info(`[${eventType}] ${eventData.event_name}`, enhancedData)
  }
}

/**
 * Initialize Google Analytics
 */
export const initGA = () => {
  if (isInitialized) return
  
  const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID
  
  // Initialize session and capture traffic source
  getOrCreateSessionId()
  captureTrafficSource()
  
  if (import.meta.env.DEV || isDebugMode) {
    logger.info('Development/Debug mode - events will be logged to console instead of sending to GA')
    isInitialized = true
    return
  }

  if (!measurementId) {
    logger.warn('GA Measurement ID not configured')
    return
  }

  // Load GA4 script
  if (typeof window !== 'undefined' && !window.gtag) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head.appendChild(script)

    window.dataLayer = window.dataLayer || []
    window.gtag = function() { window.dataLayer.push(arguments) }
    window.gtag('js', new Date())
    window.gtag('config', measurementId, {
      send_page_view: false // Manual page view tracking
    })
  }

  isInitialized = true
}

/**
 * Capture traffic source from URL parameters
 * Called once during analytics initialization
 */
const captureTrafficSource = () => {
  if (safeSessionStorage.get('traffic_source_captured')) {
    return // Already captured in this session
  }

  const params = new URLSearchParams(window.location.search)
  
  const source = {
    utm_source: params.get('utm_source') || 'direct',
    utm_medium: params.get('utm_medium') || 'none',
    utm_campaign: params.get('utm_campaign') || null,
    utm_term: params.get('utm_term') || null,
    utm_content: params.get('utm_content') || null,
    referrer: document.referrer || 'direct',
    landing_page: window.location.pathname,
    timestamp: new Date().toISOString(),
  }

  safeSessionStorage.set('traffic_source', JSON.stringify(source))
  safeSessionStorage.set('traffic_source_captured', 'true')

  if (import.meta.env.DEV || isDebugMode) {
    logger.info('Traffic source captured', source)
  }
}

/**
 * Get captured traffic source
 */
export const getTrafficSource = () => {
  const stored = safeSessionStorage.get('traffic_source')
  if (!stored) return null
  
  try {
    return JSON.parse(stored)
  } catch (error) {
    logger.error('Failed to parse traffic source', error)
    return null
  }
}

/**
 * Track page view
 */
export const trackPageView = (path, title) => {
  const eventData = {
    event_name: 'page.view',
    event_category: 'Navigation',
    event_action: 'page_view',
    event_label: title || document.title,
    page_path: path || window.location.pathname,
    page_title: title || document.title,
    page_location: window.location.href
  }

  logDebugEvent('Page View', eventData)

  if (!isDevelopment && !isDebugMode && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path || window.location.pathname,
      page_title: title || document.title
    })
  }
}

/**
 * Track generic event
 */
export const trackEvent = (category, action, label, value, customParams = {}) => {
  // Include traffic source attribution in all events
  const trafficSource = getTrafficSource()
  const attribution = trafficSource ? {
    utm_source: trafficSource.utm_source,
    utm_medium: trafficSource.utm_medium,
    utm_campaign: trafficSource.utm_campaign,
    referrer: trafficSource.referrer,
  } : {}

  // Include active A/B experiment variants in all events (ab_<experimentId>: variant)
  const experimentParams = {}
  Object.entries(activeExperimentsCache).forEach(([expId, variant]) => {
    experimentParams[`ab_${expId}`] = variant
  })

  const eventData = {
    event_name: `${category.toLowerCase()}.${action.toLowerCase()}`,
    event_category: category,
    event_action: action,
    event_label: label,
    value: value,
    ...attribution,
    ...experimentParams,
    ...customParams
  }

  logDebugEvent(category, eventData)

  if (!isDevelopment && !isDebugMode && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...attribution,
      ...experimentParams,
      ...customParams
    })
  }
}

export { isInitialized, isDevelopment, isDebugMode }

/**
 * Track error
 */
export const trackError = (errorMessage, fatal = false) => {
  trackEvent('Error', fatal ? 'fatal' : 'non_fatal', errorMessage, null, {
    error_message: errorMessage,
    is_fatal: fatal
  })
}

/**
 * Get session summary for debugging
 */
export const getSessionSummary = () => {
  const sessionStart = safeSessionStorage.get('analytics_session_start')
  return {
    session_id: safeSessionStorage.get('analytics_session_id'),
    session_start: sessionStart,
    last_activity: safeSessionStorage.get('analytics_last_activity'),
    sequence_number: safeSessionStorage.get('analytics_sequence_number'),
    duration_minutes: sessionStart 
      ? Math.floor((Date.now() - parseInt(sessionStart)) / 60000)
      : 0
  }
}

/**
 * Export session data for debugging
 */
export const exportSessionData = () => {
  return {
    ...getSessionSummary(),
    device_context: getDeviceContext(),
    user_context: getUserContext()
  }
}
